// Painter-Bibliothek: jedes Objekt der Spielwelt ist eine Zeichenfunktion mit Parametern.
// Szenen-JSON referenziert Painter per Name. Ein Painter liefert Größe + Ankerpunkt (Fuß, Mitte unten).
import { P } from './palette.ts';
import {
  Rng, type Ctx, shade, mix, rgba, vgrad, hgrad, radial, glow, dabs, brushLine, roundRect, poly,
  paintedFill, woodGrain, contactShadow, edgeLight, grain, text,
} from './paint.ts';

export interface PainterResult {
  w: number;
  h: number;
  /** Ankerpunkt innerhalb des Canvas (Standard: Mitte unten) */
  ax?: number;
  ay?: number;
}
export interface Painter {
  size(params: Record<string, any>): PainterResult;
  draw(ctx: Ctx, params: Record<string, any>, rng: Rng): void;
}

export const painters: Record<string, Painter> = {};
export function reg(name: string, p: Painter) {
  // Painter-Namen sind global: doppelte Registrierung wäre ein stilles Überschreiben fremder Grafik
  if (painters[name]) throw new Error(`Painter „${name}“ ist schon registriert`);
  painters[name] = p;
}

export const FONT_DISPLAY = '"Fraunces", Georgia, serif';
export const FONT_HAND = '"Caveat", "Comic Sans MS", cursive';

// ======================================================================
// Räume (Hintergründe)
// ======================================================================

/**
 * Innenraum mit Rückwand, Holzvertäfelung, Deckenbalken und Dielenboden.
 * params: floorY, wall, panel, floor, beams, windows[{x,y,w,h,view}], doors[{x,w,h,open}], stains
 */
reg('room', {
  size: (p) => ({ w: p.width ?? 1920, h: p.height ?? 1080, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const W = p.width ?? 1920, H = p.height ?? 1080;
    const floorY = p.floorY ?? 700;
    const wall = p.wall ?? P.plaster1;
    const panel = p.panel ?? P.sea0;
    const floor = p.floor ?? P.wood2;
    // Wand
    paintedFill(ctx, rng, () => ctx.rect(0, 0, W, floorY), [0, 0, W, floorY], wall, { light: 0.05, dark: 0.35, dabsCount: Math.round((W * floorY) / 500) });
    // Wasserflecken / Patina
    for (let i = 0; i < (p.stains ?? 9); i++) {
      const x = rng.range(0, W), y = rng.range(60, floorY * 0.6), r = rng.range(40, 160);
      radial(ctx, x, y, r, [[0, rgba(P.plaster0, 0.22)], [1, rgba(P.plaster0, 0)]]);
    }
    // Putzrisse
    for (let i = 0; i < 6; i++) {
      let x = rng.range(0, W), y = rng.range(80, floorY - 300);
      ctx.save();
      ctx.strokeStyle = rgba(P.plaster0, 0.6);
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let s = 0; s < 6; s++) {
        x += rng.range(-14, 14);
        y += rng.range(6, 18);
        ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();
    }
    // Fenster und Türen (vor der Vertäfelung)
    for (const win of p.windows ?? []) drawWindow(ctx, rng, win);
    // Vertäfelung
    const panelTop = floorY - (p.panelHeight ?? 230);
    paintedFill(ctx, rng, () => ctx.rect(0, panelTop, W, floorY - panelTop), [0, panelTop, W, floorY - panelTop], panel, { light: 0.12, dark: 0.3 });
    const pw = p.panelW ?? 160;
    for (let x = 0; x < W; x += pw) {
      ctx.save();
      ctx.strokeStyle = rgba(P.night0, 0.45);
      ctx.lineWidth = 3;
      ctx.strokeRect(x + 18, panelTop + 26, pw - 36, floorY - panelTop - 56);
      ctx.strokeStyle = rgba(P.amber3, 0.1);
      ctx.lineWidth = 2;
      ctx.strokeRect(x + 21, panelTop + 29, pw - 36, floorY - panelTop - 56);
      ctx.restore();
    }
    // Leiste
    ctx.fillStyle = shade(panel, 0.2);
    ctx.fillRect(0, panelTop - 12, W, 14);
    ctx.fillStyle = shade(panel, -0.4);
    ctx.fillRect(0, panelTop + 2, W, 4);
    for (const d of p.doors ?? []) drawDoor(ctx, rng, { ...d, floorY });
    // Sockelleiste
    ctx.fillStyle = shade(P.wood1, -0.2);
    ctx.fillRect(0, floorY - 18, W, 18);
    // Boden: Dielen zur Fluchtpunkt-Mitte
    const vx = p.vanishX ?? W / 2, vy = floorY - (p.vanishDepth ?? 900);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, floorY, W, H - floorY);
    ctx.clip();
    vgrad(ctx, 0, floorY, W, H - floorY, [[0, shade(floor, -0.35)], [0.5, floor], [1, shade(floor, -0.15)]]);
    const boards = p.boards ?? 26;
    for (let i = -boards; i <= boards * 2; i++) {
      const bx = (i / boards) * W;
      const c = rng.chance(0.5) ? shade(floor, rng.range(-0.15, 0.1)) : floor;
      ctx.fillStyle = rgba(c, 0.5);
      const bx2 = ((i + 1) / boards) * W;
      ctx.beginPath();
      ctx.moveTo(vx + (bx - vx) * ((floorY - vy) / (H - vy)), floorY);
      ctx.lineTo(vx + (bx2 - vx) * ((floorY - vy) / (H - vy)), floorY);
      ctx.lineTo(bx2, H);
      ctx.lineTo(bx, H);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = rgba(P.wood0, 0.7);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(vx + (bx - vx) * ((floorY - vy) / (H - vy)), floorY);
      ctx.lineTo(bx, H);
      ctx.stroke();
    }
    // Querfugen
    for (let k = 0; k < 7; k++) {
      const y = floorY + (H - floorY) * Math.pow(k / 7, 1.6);
      ctx.strokeStyle = rgba(P.wood0, 0.35);
      ctx.lineWidth = 1 + k * 0.3;
      for (let s = 0; s < 14; s++) {
        const x = rng.range(0, W);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + rng.range(40, 140), y);
        ctx.stroke();
      }
    }
    dabs(ctx, rng, 0, floorY, W, H - floorY, [shade(floor, 0.2), shade(floor, -0.3)], { count: 900, size: [3, 14], stretch: 4 });
    grain(ctx, 0, floorY, W, H - floorY, 0.14);
    // Wandschatten auf dem Boden
    vgrad(ctx, 0, floorY, W, 120, [[0, rgba(P.night0, 0.55)], [1, rgba(P.night0, 0)]]);
    ctx.restore();
    // Deckenbalken
    for (const bx of p.beams ?? []) {
      ctx.save();
      woodGrain(ctx, rng, bx - 40, 0, 80, 60, P.wood1, false);
      ctx.fillStyle = rgba(P.night0, 0.4);
      ctx.fillRect(bx - 40, 56, 80, 10);
      ctx.restore();
    }
    if (p.ceiling !== false) {
      woodGrain(ctx, rng, 0, 0, W, 54, P.wood1, true);
      vgrad(ctx, 0, 54, W, 70, [[0, rgba(P.night0, 0.6)], [1, rgba(P.night0, 0)]]);
    }
    // Ecken abdunkeln
    hgrad(ctx, 0, 0, 260, H, [[0, rgba(P.night0, 0.55)], [1, rgba(P.night0, 0)]]);
    hgrad(ctx, W - 260, 0, 260, H, [[0, rgba(P.night0, 0)], [1, rgba(P.night0, 0.55)]]);
  },
});

export function drawWindow(ctx: Ctx, rng: Rng, w: { x: number; y: number; w: number; h: number; view?: string; arch?: boolean }) {
  const { x, y } = w;
  const ww = w.w, hh = w.h;
  // Rahmen-Schatten
  ctx.save();
  ctx.fillStyle = rgba(P.night0, 0.5);
  ctx.fillRect(x - 26, y - 22, ww + 52, hh + 50);
  // Nachtszene Hafen
  ctx.beginPath();
  ctx.rect(x, y, ww, hh);
  ctx.clip();
  vgrad(ctx, x, y, ww, hh, [[0, P.night0], [0.55, P.night2], [0.62, P.night3], [0.7, P.sea0], [1, P.night0]]);
  // Sterne
  for (let i = 0; i < 25; i++) {
    ctx.fillStyle = rgba(P.amber3, rng.range(0.2, 0.7));
    ctx.fillRect(x + rng.next() * ww, y + rng.next() * hh * 0.45, 1.5, 1.5);
  }
  // Mond
  glow(ctx, x + ww * 0.75, y + hh * 0.18, 60, P.amber3, 0.25);
  ctx.fillStyle = mix(P.paper, P.amber3, 0.5);
  ctx.beginPath();
  ctx.arc(x + ww * 0.75, y + hh * 0.18, 13, 0, Math.PI * 2);
  ctx.fill();
  // Gegenüberliegende Kaimauer & Häuser
  const horizon = y + hh * 0.62;
  for (let i = 0; i < 9; i++) {
    const bx = x + (i / 9) * ww - 10 + rng.range(-8, 8);
    const bh = rng.range(30, 80);
    ctx.fillStyle = mix(P.night1, P.night0, rng.next());
    ctx.fillRect(bx, horizon - bh, ww / 9 + 14, bh);
    poly(ctx, [bx - 4, horizon - bh, bx + ww / 18 + 7, horizon - bh - rng.range(14, 30), bx + ww / 9 + 18, horizon - bh]);
    ctx.fill();
    for (let k = 0; k < 3; k++) {
      if (rng.chance(0.5)) {
        const lx = bx + rng.range(6, ww / 9), ly = horizon - rng.range(10, bh - 6);
        ctx.fillStyle = P.amber2;
        ctx.fillRect(lx, ly, 5, 6);
        glow(ctx, lx + 2, ly + 3, 14, P.amber1, 0.35);
      }
    }
  }
  // Kran
  if (w.view !== 'yard') {
    ctx.strokeStyle = P.night0;
    ctx.lineWidth = 4;
    const cx = x + ww * 0.3;
    ctx.beginPath();
    ctx.moveTo(cx, horizon - 40);
    ctx.lineTo(cx, horizon - 150);
    ctx.lineTo(cx + 110, horizon - 140);
    ctx.moveTo(cx, horizon - 150);
    ctx.lineTo(cx - 30, horizon - 130);
    ctx.stroke();
    ctx.fillStyle = P.red2;
    ctx.fillRect(cx - 2, horizon - 154, 4, 4);
    glow(ctx, cx, horizon - 152, 16, P.red2, 0.5);
  }
  // Wasserreflexe
  for (let i = 0; i < 40; i++) {
    const rx = x + rng.next() * ww, ry = horizon + rng.range(4, hh * 0.35);
    ctx.fillStyle = rgba(P.amber2, rng.range(0.15, 0.5));
    ctx.fillRect(rx, ry, rng.range(6, 20), 1.6);
  }
  // Regen-/Kondensschlieren auf der Scheibe
  for (let i = 0; i < 30; i++) {
    const rx = x + rng.next() * ww, ry = y + rng.next() * hh;
    ctx.strokeStyle = rgba(P.white, 0.12);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(rx, ry);
    ctx.lineTo(rx + rng.range(-2, 2), ry + rng.range(8, 30));
    ctx.stroke();
  }
  // Spiegelung
  ctx.fillStyle = rgba(P.white, 0.05);
  poly(ctx, [x, y, x + ww * 0.3, y, x, y + hh * 0.5]);
  ctx.fill();
  ctx.restore();
  // Rahmen und Sprossen
  const frame = P.wood1;
  ctx.fillStyle = frame;
  ctx.fillRect(x - 16, y - 14, ww + 32, 16);
  ctx.fillRect(x - 16, y + hh, ww + 32, 26);
  ctx.fillRect(x - 16, y - 14, 16, hh + 40);
  ctx.fillRect(x + ww, y - 14, 16, hh + 40);
  ctx.fillRect(x + ww / 2 - 5, y, 10, hh);
  ctx.fillRect(x, y + hh / 2 - 5, ww, 10);
  edgeLight(ctx, x - 16, y + hh, ww + 32, 26, frame, 0.3, 0.4);
  // Fensterbank-Licht
  ctx.fillStyle = rgba(P.night3, 0.35);
  ctx.fillRect(x - 16, y + hh, ww + 32, 4);
}

export function drawDoor(ctx: Ctx, rng: Rng, d: { x: number; w?: number; h?: number; floorY: number; open?: boolean; color?: string; sign?: string }) {
  const w = d.w ?? 170, h = d.h ?? 420;
  const x = d.x - w / 2, y = d.floorY - h;
  ctx.save();
  ctx.fillStyle = rgba(P.night0, 0.6);
  ctx.fillRect(x - 22, y - 22, w + 44, h + 22);
  ctx.fillStyle = P.wood1;
  ctx.fillRect(x - 16, y - 16, w + 32, h + 16);
  if (d.open) {
    vgrad(ctx, x, y, w, h, [[0, P.night0], [1, P.night1]]);
  } else {
    const col = d.color ?? P.wood3;
    for (let i = 0; i < 4; i++) woodGrain(ctx, rng, x + (i * w) / 4, y, w / 4, h, shade(col, rng.range(-0.1, 0.05)), false);
    ctx.strokeStyle = rgba(P.ink, 0.5);
    ctx.lineWidth = 2;
    for (let i = 1; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(x + (i * w) / 4, y);
      ctx.lineTo(x + (i * w) / 4, y + h);
      ctx.stroke();
    }
    // Querbretter (Z-Tür)
    ctx.fillStyle = shade(col, -0.2);
    ctx.fillRect(x, y + 50, w, 26);
    ctx.fillRect(x, y + h - 80, w, 26);
    // Klinke
    ctx.fillStyle = P.brass1;
    ctx.beginPath();
    ctx.arc(x + w - 26, y + h / 2, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(x + w - 44, y + h / 2 - 3, 20, 6);
    ctx.fillStyle = P.brass2;
    ctx.fillRect(x + w - 44, y + h / 2 - 3, 20, 2);
    vgrad(ctx, x, y, w, h, [[0, rgba(P.night0, 0)], [1, rgba(P.night0, 0.35)]]);
  }
  if (d.sign) {
    roundRect(ctx, x + w / 2 - 60, y + 100, 120, 40, 4);
    ctx.fillStyle = P.paper;
    ctx.fill();
    text(ctx, d.sign, x + w / 2, y + 121, `600 20px ${FONT_HAND}`, P.ink);
  }
  ctx.restore();
}

// ======================================================================
// Möbel & Objekte
// ======================================================================

/** Lange Theke mit Messingleiste; params: w, h */
reg('counter', {
  size: (p) => ({ w: (p.w ?? 900) + 40, h: (p.h ?? 260) + 40 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 900, h = p.h ?? 260;
    const x = 20, y = 20;
    contactShadow(ctx, x + w / 2, y + h + 10, w + 60, 50, 0.55);
    // Front: senkrechte Bretter mit Füllungen
    const n = Math.round(w / 110);
    for (let i = 0; i < n; i++) woodGrain(ctx, rng, x + (i * w) / n, y + 36, w / n, h - 36, shade(P.wood2, rng.range(-0.08, 0.04)), false);
    for (let i = 0; i < n; i++) {
      const px = x + (i * w) / n + 14, pw = w / n - 28;
      ctx.strokeStyle = rgba(P.wood0, 0.75);
      ctx.lineWidth = 4;
      ctx.strokeRect(px, y + 70, pw, h - 120);
      ctx.strokeStyle = rgba(P.amber2, 0.12);
      ctx.lineWidth = 2;
      ctx.strokeRect(px + 3, y + 73, pw, h - 120);
    }
    vgrad(ctx, x, y + 36, w, h - 36, [[0, rgba(P.amber1, 0.12)], [0.4, rgba(P.night0, 0)], [1, rgba(P.night0, 0.5)]]);
    // Platte
    ctx.fillStyle = P.wood1;
    roundRect(ctx, x - 16, y, w + 32, 40, 6);
    ctx.fill();
    woodGrain(ctx, rng, x - 12, y + 2, w + 24, 22, P.wood3, true);
    ctx.fillStyle = rgba(P.amber3, 0.25);
    ctx.fillRect(x - 12, y + 2, w + 24, 3);
    // Messing-Fußleiste
    ctx.fillStyle = P.brass1;
    ctx.fillRect(x + 10, y + h - 40, w - 20, 9);
    ctx.fillStyle = P.brass2;
    ctx.fillRect(x + 10, y + h - 40, w - 20, 3);
    for (let i = 0; i <= 6; i++) {
      ctx.fillStyle = P.brass0;
      ctx.fillRect(x + 20 + (i * (w - 40)) / 6, y + h - 32, 6, 32);
    }
    // Kerben & Gebrauchsspuren
    for (let i = 0; i < 18; i++) brushLine(ctx, rng, x + rng.next() * w, y + 8 + rng.next() * 14, x + rng.next() * w, y + 10 + rng.next() * 12, rgba(P.wood0, 0.5), 1, 1);
  },
});

/** Zapfanlage; params: taps */
reg('beertap', {
  size: () => ({ w: 200, h: 170 }),
  draw(ctx, p, rng) {
    const n = p.taps ?? 3;
    ctx.fillStyle = P.brass0;
    roundRect(ctx, 20, 100, 160, 22, 8);
    ctx.fill();
    ctx.fillStyle = P.brass1;
    roundRect(ctx, 20, 98, 160, 12, 6);
    ctx.fill();
    for (let i = 0; i < n; i++) {
      const cx = 40 + (i * 120) / (n - 1 || 1);
      ctx.fillStyle = P.brass1;
      ctx.fillRect(cx - 7, 40, 14, 62);
      ctx.fillStyle = P.brass2;
      ctx.fillRect(cx - 7, 40, 4, 62);
      // Hebel
      ctx.fillStyle = [P.red1, P.ink, P.sea1][i % 3];
      roundRect(ctx, cx - 9, 0, 18, 44, 6);
      ctx.fill();
      ctx.fillStyle = rgba(P.white, 0.25);
      ctx.fillRect(cx - 6, 4, 4, 34);
      // Auslauf
      ctx.fillStyle = P.brass1;
      ctx.fillRect(cx - 4, 120, 8, 22);
    }
    // Tropfschale
    ctx.fillStyle = P.iron1;
    ctx.fillRect(10, 150, 180, 18);
    ctx.fillStyle = P.iron2;
    ctx.fillRect(10, 150, 180, 4);
    if (p.dull) {
      ctx.fillStyle = rgba(P.wood0, 0.45);
      ctx.fillRect(0, 0, 200, 170);
    } else glow(ctx, 60, 60, 40, P.amber2, 0.15);
    void rng;
  },
});

/** Wandregal mit Flaschen; params: w, rows */
reg('bottleshelf', {
  size: (p) => ({ w: p.w ?? 520, h: (p.rows ?? 3) * 120 + 20 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 520, rows = p.rows ?? 3;
    // Rückwand (Spiegel)
    ctx.fillStyle = rgba(P.night0, 0.4);
    ctx.fillRect(0, 0, w, rows * 120 + 20);
    vgrad(ctx, 8, 8, w - 16, rows * 120, [[0, rgba(P.sea1, 0.5)], [1, rgba(P.night0, 0.6)]]);
    for (let r = 0; r < rows; r++) {
      const by = (r + 1) * 120;
      let x = 20;
      while (x < w - 40) {
        const bw = rng.range(22, 38), bh = rng.range(60, 100);
        if (p.sparse && rng.chance(0.65)) {
          x += bw + rng.range(4, 14);
          continue;
        }
        const col = rng.pick([P.green1, P.red0, P.amber0, P.sea1, P.brass0, P.green0, P.wood2]);
        drawBottle(ctx, rng, x + bw / 2, by, bw, bh, col);
        x += bw + rng.range(4, 14);
      }
      woodGrain(ctx, rng, 0, by, w, 14, P.wood2, true);
      ctx.fillStyle = rgba(P.night0, 0.5);
      ctx.fillRect(0, by + 14, w, 6);
    }
    ctx.fillStyle = P.wood1;
    ctx.fillRect(0, 0, 10, rows * 120 + 20);
    ctx.fillRect(w - 10, 0, 10, rows * 120 + 20);
  },
});

function drawBottle(ctx: Ctx, rng: Rng, cx: number, by: number, bw: number, bh: number, col: string) {
  const neck = bh * rng.range(0.3, 0.45);
  ctx.save();
  ctx.fillStyle = col;
  ctx.globalAlpha = 0.9;
  ctx.beginPath();
  ctx.moveTo(cx - bw / 2, by);
  ctx.lineTo(cx - bw / 2, by - bh + neck + 8);
  ctx.quadraticCurveTo(cx - bw / 2, by - bh + neck - 4, cx - bw / 6, by - bh + neck - 10);
  ctx.lineTo(cx - bw / 6, by - bh);
  ctx.lineTo(cx + bw / 6, by - bh);
  ctx.lineTo(cx + bw / 6, by - bh + neck - 10);
  ctx.quadraticCurveTo(cx + bw / 2, by - bh + neck - 4, cx + bw / 2, by - bh + neck + 8);
  ctx.lineTo(cx + bw / 2, by);
  ctx.closePath();
  ctx.fill();
  // Etikett
  if (rng.chance(0.7)) {
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = rng.pick([P.paper, P.amber2, P.white]);
    ctx.fillRect(cx - bw / 2 + 3, by - bh * 0.45, bw - 6, bh * 0.22);
  }
  // Glanz
  ctx.globalAlpha = 0.5;
  ctx.fillStyle = P.amber3;
  ctx.fillRect(cx - bw / 2 + 4, by - bh + neck + 6, 3, bh - neck - 14);
  ctx.restore();
}

/** Barhocker */
reg('stool', {
  size: () => ({ w: 110, h: 190 }),
  draw(ctx, _p, rng) {
    if (_p.broken) {
      ctx.translate(55, 186);
      ctx.rotate(0.35);
      ctx.translate(-55, -186);
    }
    contactShadow(ctx, 55, 184, 100, 18, 0.5);
    ctx.strokeStyle = P.iron1;
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(30, 186);
    ctx.lineTo(45, 40);
    ctx.moveTo(80, 186);
    ctx.lineTo(65, 40);
    ctx.stroke();
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(34, 130);
    ctx.lineTo(76, 130);
    ctx.stroke();
    ctx.strokeStyle = P.iron2;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(28, 186);
    ctx.lineTo(43, 40);
    ctx.stroke();
    // Sitz
    ctx.fillStyle = P.red0;
    ctx.beginPath();
    ctx.ellipse(55, 34, 46, 16, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.red1;
    ctx.beginPath();
    ctx.ellipse(55, 28, 44, 14, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = rgba(P.amber3, 0.25);
    ctx.beginPath();
    ctx.ellipse(45, 24, 22, 5, -0.1, 0, Math.PI * 2);
    ctx.fill();
    // Riss im Polster
    brushLine(ctx, rng, 60, 24, 80, 30, P.paper, 2, 2);
  },
});

/** Runder Tisch; params: r */
reg('table', {
  size: (p) => ({ w: (p.r ?? 120) * 2 + 40, h: 230 }),
  draw(ctx, p, rng) {
    const r = p.r ?? 120, cx = r + 20;
    contactShadow(ctx, cx, 222, r * 1.8, 30, 0.55);
    ctx.fillStyle = P.wood1;
    ctx.fillRect(cx - 12, 60, 24, 150);
    ctx.fillStyle = P.wood0;
    ctx.beginPath();
    ctx.ellipse(cx, 214, 60, 14, 0, 0, Math.PI * 2);
    ctx.fill();
    // Platte
    ctx.fillStyle = P.wood1;
    ctx.beginPath();
    ctx.ellipse(cx, 62, r, r * 0.32, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(cx, 54, r, r * 0.32, 0, 0, Math.PI * 2);
    ctx.clip();
    woodGrain(ctx, rng, cx - r, 54 - r * 0.32, r * 2, r * 0.64, P.wood3, true);
    radial(ctx, cx - r * 0.2, 44, r, [[0, rgba(P.amber2, 0.25)], [1, rgba(P.amber2, 0)]]);
    ctx.restore();
    // Bierdeckel / Gläser
    for (const it of p.items ?? []) {
      if (it === 'coaster') {
        ctx.fillStyle = P.paper;
        ctx.beginPath();
        ctx.ellipse(cx - r * 0.4, 52, 18, 6, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = P.red1;
        ctx.beginPath();
        ctx.ellipse(cx - r * 0.4, 52, 10, 3, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      if (it === 'glass') drawGlass(ctx, cx + r * 0.3, 56, 0.6);
      if (it === 'candle') {
        ctx.fillStyle = P.green0;
        ctx.fillRect(cx - 9, 10, 18, 44);
        ctx.fillStyle = P.paper;
        ctx.fillRect(cx - 4, 0, 8, 14);
        ctx.fillStyle = P.amber3;
        ctx.beginPath();
        ctx.ellipse(cx, -10, 5, 11, 0, 0, Math.PI * 2);
        ctx.fill();
        glow(ctx, cx, -8, 40, P.amber2, 0.5);
      }
    }
  },
});

function drawGlass(ctx: Ctx, x: number, by: number, fill: number) {
  ctx.save();
  ctx.fillStyle = rgba(P.white, 0.18);
  ctx.fillRect(x - 12, by - 46, 24, 46);
  ctx.fillStyle = rgba(P.amber1, 0.85);
  ctx.fillRect(x - 11, by - 46 * fill, 22, 46 * fill);
  ctx.fillStyle = P.white;
  ctx.globalAlpha = 0.8;
  ctx.fillRect(x - 11, by - 46 * fill - 6, 22, 7);
  ctx.globalAlpha = 0.5;
  ctx.fillRect(x - 9, by - 42, 3, 36);
  ctx.restore();
}

/** Stuhl, params: flip */
reg('chair', {
  size: () => ({ w: 120, h: 230 }),
  draw(ctx, p, rng) {
    if (p.flip) {
      ctx.translate(120, 0);
      ctx.scale(-1, 1);
    }
    contactShadow(ctx, 60, 224, 100, 18, 0.5);
    ctx.fillStyle = P.wood1;
    ctx.fillRect(20, 120, 10, 104);
    ctx.fillRect(88, 120, 10, 104);
    ctx.fillRect(30, 130, 8, 90);
    // Lehne
    woodGrain(ctx, rng, 20, 0, 14, 130, P.wood2, false);
    woodGrain(ctx, rng, 26, 10, 64, 18, P.wood3, true);
    woodGrain(ctx, rng, 26, 46, 64, 12, P.wood3, true);
    // Sitz
    ctx.fillStyle = P.wood2;
    poly(ctx, [14, 116, 104, 116, 112, 134, 8, 134]);
    ctx.fill();
    ctx.fillStyle = shade(P.wood3, 0.1);
    poly(ctx, [14, 116, 104, 116, 106, 121, 12, 121]);
    ctx.fill();
  },
});

/** Wanduhr (Schiffsuhr mit Bullaugen-Rahmen); params: time "HH:MM" */
reg('clock', {
  size: () => ({ w: 140, h: 140, ax: 70, ay: 70 }),
  draw(ctx, p) {
    const cx = 70, cy = 70;
    ctx.fillStyle = rgba(P.night0, 0.45);
    ctx.beginPath();
    ctx.arc(cx + 5, cy + 7, 62, 0, Math.PI * 2);
    ctx.fill();
    const g = ctx.createRadialGradient(cx - 20, cy - 20, 5, cx, cy, 64);
    g.addColorStop(0, P.brass2);
    g.addColorStop(0.6, P.brass1);
    g.addColorStop(1, P.brass0);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, 62, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      ctx.fillStyle = P.brass0;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(a) * 55, cy + Math.sin(a) * 55, 3.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = P.paper;
    ctx.beginPath();
    ctx.arc(cx, cy, 46, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      ctx.strokeStyle = P.ink;
      ctx.lineWidth = i % 3 === 0 ? 3 : 1.5;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * 38, cy + Math.sin(a) * 38);
      ctx.lineTo(cx + Math.cos(a) * 44, cy + Math.sin(a) * 44);
      ctx.stroke();
    }
    const [hh, mm] = String(p.time ?? '11:52').split(':').map(Number);
    const ma = (mm / 60) * Math.PI * 2 - Math.PI / 2;
    const ha = (((hh % 12) + mm / 60) / 12) * Math.PI * 2 - Math.PI / 2;
    ctx.strokeStyle = P.ink;
    ctx.lineCap = 'round';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(ha) * 24, cy + Math.sin(ha) * 24);
    ctx.stroke();
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(ma) * 36, cy + Math.sin(ma) * 36);
    ctx.stroke();
    ctx.fillStyle = P.red1;
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = rgba(P.white, 0.25);
    ctx.beginPath();
    ctx.ellipse(cx - 16, cy - 20, 18, 8, -0.6, 0, Math.PI * 2);
    ctx.fill();
  },
});

/** Dartscheibe */
reg('dartboard', {
  size: () => ({ w: 150, h: 150, ax: 75, ay: 75 }),
  draw(ctx, p, rng) {
    const cx = 75, cy = 75;
    ctx.fillStyle = rgba(P.night0, 0.5);
    ctx.beginPath();
    ctx.arc(cx + 5, cy + 6, 68, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.ink;
    ctx.beginPath();
    ctx.arc(cx, cy, 68, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < 20; i++) {
      const a0 = (i / 20) * Math.PI * 2 - Math.PI / 20, a1 = a0 + Math.PI / 10;
      for (const [r0, r1, c1, c2] of [
        [8, 54, P.ink, P.paper],
        [50, 54, P.red1, P.green1],
        [30, 34, P.red1, P.green1],
      ] as [number, number, string, string][]) {
        ctx.fillStyle = i % 2 ? c1 : c2;
        ctx.beginPath();
        ctx.arc(cx, cy, r1, a0, a1);
        ctx.arc(cx, cy, r0, a1, a0, true);
        ctx.closePath();
        ctx.fill();
      }
    }
    ctx.fillStyle = P.green1;
    ctx.beginPath();
    ctx.arc(cx, cy, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.red1;
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fill();
    // Pfeile
    for (const [dx, dy] of p.darts ?? [[-20, -12], [26, 18]]) {
      ctx.strokeStyle = P.iron2;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(cx + dx, cy + dy);
      ctx.lineTo(cx + dx + 22, cy + dy - 14);
      ctx.stroke();
      ctx.fillStyle = P.red2;
      poly(ctx, [cx + dx + 20, cy + dy - 14, cx + dx + 34, cy + dy - 26, cx + dx + 30, cy + dy - 12]);
      ctx.fill();
    }
    grain(ctx, 0, 0, 150, 150, 0.15);
    void rng;
  },
});

/** Rettungsring */
reg('lifebuoy', {
  size: () => ({ w: 150, h: 170, ax: 75, ay: 85 }),
  draw(ctx, p) {
    const cx = 75, cy = 85;
    ctx.strokeStyle = rgba(P.night0, 0.45);
    ctx.lineWidth = 28;
    ctx.beginPath();
    ctx.arc(cx + 6, cy + 8, 50, 0, Math.PI * 2);
    ctx.stroke();
    for (let i = 0; i < 8; i++) {
      ctx.strokeStyle = i % 2 ? P.red1 : P.white;
      ctx.lineWidth = 28;
      ctx.beginPath();
      ctx.arc(cx, cy, 50, (i / 8) * Math.PI * 2, ((i + 1) / 8) * Math.PI * 2);
      ctx.stroke();
    }
    ctx.strokeStyle = rgba(P.night0, 0.35);
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.arc(cx, cy, 58, 0.2, Math.PI * 0.9);
    ctx.stroke();
    ctx.strokeStyle = rgba(P.amber3, 0.3);
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.arc(cx, cy, 44, Math.PI * 1.1, Math.PI * 1.6);
    ctx.stroke();
    // Seil
    ctx.strokeStyle = P.plaster2;
    ctx.lineWidth = 3;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.arc(cx, cy, 50, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    text(ctx, p.name ?? 'MÖWE', cx, cy - 50, `700 13px ${FONT_DISPLAY}`, P.ink);
  },
});

/** Schiffsmodell in einer Vitrine/auf Regal */
reg('shipmodel', {
  size: () => ({ w: 260, h: 220 }),
  draw(ctx, p, rng) {
    // Brett
    woodGrain(ctx, rng, 0, 196, 260, 24, P.wood2, true);
    ctx.fillStyle = rgba(P.night0, 0.5);
    ctx.fillRect(0, 214, 260, 6);
    // Rumpf
    ctx.fillStyle = P.wood1;
    ctx.beginPath();
    ctx.moveTo(30, 150);
    ctx.lineTo(236, 150);
    ctx.quadraticCurveTo(226, 186, 190, 190);
    ctx.lineTo(70, 190);
    ctx.quadraticCurveTo(40, 186, 30, 150);
    ctx.fill();
    ctx.fillStyle = P.red1;
    ctx.fillRect(48, 176, 160, 7);
    ctx.fillStyle = P.paper;
    ctx.fillRect(40, 150, 190, 5);
    // Masten und Segel
    for (const [mx, h] of [[90, 120], [150, 135], [200, 95]]) {
      ctx.fillStyle = P.wood1;
      ctx.fillRect(mx - 2, 150 - h, 4, h);
      for (let s = 0; s < 3; s++) {
        const sy = 150 - h + 12 + s * (h / 3.4);
        ctx.fillStyle = mix(P.paper, P.plaster2, s * 0.25);
        ctx.beginPath();
        ctx.moveTo(mx - 26 + s * 2, sy);
        ctx.lineTo(mx + 26 - s * 2, sy);
        ctx.quadraticCurveTo(mx + 30, sy + h / 8, mx + 24, sy + h / 4.2);
        ctx.lineTo(mx - 24, sy + h / 4.2);
        ctx.quadraticCurveTo(mx - 30, sy + h / 8, mx - 26 + s * 2, sy);
        ctx.fill();
      }
    }
    // Takelage
    ctx.strokeStyle = rgba(P.ink, 0.55);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(236, 150);
    ctx.lineTo(150, 15);
    ctx.lineTo(90, 30);
    ctx.lineTo(30, 150);
    ctx.stroke();
    // Fähnchen
    ctx.fillStyle = P.red2;
    poly(ctx, [150, 15, 172, 21, 150, 27]);
    ctx.fill();
    if (p.bottle) {
      ctx.fillStyle = rgba(P.sea2, 0.18);
      roundRect(ctx, 14, 30, 232, 170, 60);
      ctx.fill();
    }
    grain(ctx, 0, 0, 260, 220, 0.12);
  },
});

/** Kreidetafel; params: lines[] (Texte werden vom Spiel per i18n gesetzt) */
reg('chalkboard', {
  size: (p) => ({ w: p.w ?? 260, h: p.h ?? 340 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 260, h = p.h ?? 340;
    ctx.fillStyle = rgba(P.night0, 0.45);
    ctx.fillRect(10, 12, w, h);
    woodGrain(ctx, rng, 0, 0, w, h, P.wood2, false);
    ctx.fillStyle = P.green0;
    ctx.fillRect(14, 14, w - 28, h - 28);
    dabs(ctx, rng, 14, 14, w - 28, h - 28, [rgba(P.white, 0.6), P.green1], { count: 160, size: [4, 16], alpha: [0.03, 0.08] });
    const lines: string[] = p.lines ?? [];
    lines.forEach((l, i) => {
      const big = i === 0;
      text(ctx, l, w / 2 + rng.range(-3, 3), 50 + i * (big ? 0 : 40) + (big ? 0 : 30), `${big ? 700 : 600} ${big ? 34 : 26}px ${FONT_HAND}`, rgba(P.white, 0.88));
    });
    // Bierglas-Zeichnung
    ctx.strokeStyle = rgba(P.white, 0.7);
    ctx.lineWidth = 2.5;
    ctx.strokeRect(w - 70, h - 90, 34, 50);
    ctx.beginPath();
    ctx.arc(w - 36, h - 66, 10, -Math.PI / 2, Math.PI / 2);
    ctx.stroke();
  },
});

/** Wandtresor hinter Bilderrahmen; params.state: 'hidden'|'closed'|'open' */
reg('safe', {
  size: () => ({ w: 200, h: 200 }),
  draw(ctx, p, rng) {
    const st = p.state ?? 'closed';
    ctx.fillStyle = rgba(P.night0, 0.6);
    ctx.fillRect(6, 10, 196, 194);
    ctx.fillStyle = P.iron0;
    ctx.fillRect(0, 0, 196, 196);
    if (st === 'open') {
      vgrad(ctx, 18, 18, 160, 160, [[0, P.night0], [1, P.iron0]]);
      // Tür aufgeklappt
      ctx.fillStyle = P.iron1;
      poly(ctx, [0, 0, -60, 20, -60, 176, 0, 196]);
      ctx.fill();
      ctx.fillStyle = P.iron2;
      ctx.fillRect(-36, 80, 12, 36);
      // Inhalt: Brief + Schlüssel (falls nicht genommen)
      if (!p.empty) {
        ctx.fillStyle = P.paper;
        ctx.save();
        ctx.translate(98, 140);
        ctx.rotate(-0.08);
        ctx.fillRect(-50, -26, 100, 52);
        ctx.strokeStyle = P.red1;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-50, -26);
        ctx.lineTo(0, 4);
        ctx.lineTo(50, -26);
        ctx.stroke();
        ctx.restore();
        ctx.fillStyle = P.brass1;
        ctx.beginPath();
        ctx.arc(130, 92, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(66, 89, 64, 6);
        ctx.fillRect(66, 95, 6, 12);
        ctx.fillRect(78, 95, 6, 8);
        glow(ctx, 110, 100, 50, P.amber2, 0.25);
      }
      return;
    }
    const g = ctx.createLinearGradient(0, 0, 196, 196);
    g.addColorStop(0, P.iron2);
    g.addColorStop(1, P.iron0);
    ctx.fillStyle = g;
    ctx.fillRect(10, 10, 176, 176);
    dabs(ctx, rng, 10, 10, 176, 176, [P.wood3, P.iron1], { count: 60, size: [3, 10], alpha: [0.1, 0.3] });
    // Drei Zahlenräder
    for (let i = 0; i < 3; i++) {
      const cx = 52 + i * 46, cy = 70;
      ctx.fillStyle = P.iron0;
      ctx.beginPath();
      ctx.arc(cx, cy, 18, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = (p.solved ?? 0) > i ? P.amber2 : P.brass1;
      ctx.beginPath();
      ctx.arc(cx, cy, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = P.brass0;
      ctx.lineWidth = 2;
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * 9, cy + Math.sin(a) * 9);
        ctx.lineTo(cx + Math.cos(a) * 14, cy + Math.sin(a) * 14);
        ctx.stroke();
      }
    }
    // Griff
    ctx.fillStyle = P.brass1;
    ctx.fillRect(84, 120, 28, 40);
    ctx.fillStyle = P.brass2;
    ctx.fillRect(84, 120, 28, 6);
    text(ctx, 'H. J. 1922', 98, 176, `600 13px ${FONT_DISPLAY}`, P.brass2);
  },
});

/** Bilderrahmen; params: subject 'captain'|'ship'|'harbor', state: 'hung'|'aside' */
reg('frame', {
  size: (p) => ({ w: p.w ?? 200, h: p.h ?? 240 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 200, h = p.h ?? 240;
    if (p.state === 'aside') return;
    ctx.fillStyle = rgba(P.night0, 0.5);
    ctx.fillRect(8, 10, w, h);
    const fg = ctx.createLinearGradient(0, 0, w, h);
    fg.addColorStop(0, P.brass2);
    fg.addColorStop(0.5, P.brass1);
    fg.addColorStop(1, P.brass0);
    ctx.fillStyle = fg;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = P.brass0;
    ctx.fillRect(14, 14, w - 28, h - 28);
    const ix = 20, iy = 20, iw = w - 40, ih = h - 40;
    ctx.save();
    ctx.beginPath();
    ctx.rect(ix, iy, iw, ih);
    ctx.clip();
    if ((p.subject ?? 'captain') === 'captain') {
      vgrad(ctx, ix, iy, iw, ih, [[0, P.sea1], [1, P.sea0]]);
      dabs(ctx, rng, ix, iy, iw, ih, [P.sea2, P.night1], { count: 80, size: [6, 18] });
      const cx = ix + iw / 2;
      // Schultern / Mantel
      ctx.fillStyle = P.night1;
      ctx.beginPath();
      ctx.ellipse(cx, iy + ih + 10, iw * 0.48, ih * 0.38, 0, Math.PI, 0);
      ctx.fill();
      ctx.fillStyle = P.brass1;
      for (let i = 0; i < 3; i++) ctx.fillRect(cx - 3, iy + ih * 0.78 + i * 14, 6, 6);
      // Kopf
      ctx.fillStyle = P.skin1;
      ctx.beginPath();
      ctx.ellipse(cx, iy + ih * 0.46, iw * 0.2, ih * 0.17, 0, 0, Math.PI * 2);
      ctx.fill();
      // Bart
      ctx.fillStyle = P.plaster2;
      ctx.beginPath();
      ctx.ellipse(cx, iy + ih * 0.56, iw * 0.21, ih * 0.12, 0, 0, Math.PI);
      ctx.fill();
      // Mütze
      ctx.fillStyle = P.night0;
      ctx.fillRect(cx - iw * 0.24, iy + ih * 0.26, iw * 0.48, ih * 0.1);
      ctx.fillStyle = P.ink;
      ctx.fillRect(cx - iw * 0.22, iy + ih * 0.34, iw * 0.5, ih * 0.03);
      ctx.fillStyle = P.brass2;
      ctx.beginPath();
      ctx.arc(cx, iy + ih * 0.31, 5, 0, Math.PI * 2);
      ctx.fill();
      // Augen
      ctx.fillStyle = P.ink;
      ctx.fillRect(cx - iw * 0.09, iy + ih * 0.44, 6, 4);
      ctx.fillRect(cx + iw * 0.05, iy + ih * 0.44, 6, 4);
    } else {
      vgrad(ctx, ix, iy, iw, ih, [[0, P.amber2], [0.6, P.red2], [0.62, P.sea1], [1, P.sea0]]);
      ctx.fillStyle = P.night0;
      ctx.beginPath();
      ctx.moveTo(ix + iw * 0.2, iy + ih * 0.7);
      ctx.lineTo(ix + iw * 0.8, iy + ih * 0.7);
      ctx.lineTo(ix + iw * 0.7, iy + ih * 0.78);
      ctx.lineTo(ix + iw * 0.28, iy + ih * 0.78);
      ctx.fill();
      ctx.fillRect(ix + iw * 0.48, iy + ih * 0.3, 4, ih * 0.4);
    }
    // Firnis / Alterung
    radial(ctx, ix + iw / 2, iy + ih / 2, iw, [[0, rgba(P.amber1, 0.12)], [1, rgba(P.wood0, 0.45)]]);
    grain(ctx, ix, iy, iw, ih, 0.2);
    ctx.restore();
    if (p.tilted) {
      // kleiner Spalt: Tresor dahinter erahnbar
      ctx.fillStyle = rgba(P.night0, 0.4);
      ctx.fillRect(w - 6, 0, 6, h);
    }
  },
});

/** Jukebox */
reg('jukebox', {
  size: () => ({ w: 190, h: 300 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 95, 296, 190, 26, 0.6);
    ctx.fillStyle = P.wood1;
    roundRect(ctx, 6, 10, 178, 288, 80);
    ctx.fill();
    ctx.save();
    roundRect(ctx, 6, 10, 178, 288, 80);
    ctx.clip();
    woodGrain(ctx, rng, 6, 10, 178, 288, P.wood2, false);
    ctx.restore();
    // Glasbogen
    const lit = p.state === 'on';
    ctx.fillStyle = lit ? P.amber1 : P.amber0;
    roundRect(ctx, 22, 26, 146, 120, 66);
    ctx.fill();
    ctx.fillStyle = lit ? P.amber3 : rgba(P.amber2, 0.5);
    roundRect(ctx, 36, 40, 118, 92, 54);
    ctx.fill();
    // Plattenteller-Fenster
    ctx.fillStyle = P.night1;
    roundRect(ctx, 36, 150, 118, 64, 8);
    ctx.fill();
    ctx.fillStyle = P.ink;
    ctx.beginPath();
    ctx.ellipse(95, 186, 40, 12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.red1;
    ctx.beginPath();
    ctx.ellipse(95, 186, 10, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    // Grill
    ctx.strokeStyle = P.brass1;
    ctx.lineWidth = 3;
    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      ctx.moveTo(40, 228 + i * 10);
      ctx.lineTo(150, 228 + i * 10);
      ctx.stroke();
    }
    // Chromleisten
    ctx.strokeStyle = P.brass2;
    ctx.lineWidth = 4;
    roundRect(ctx, 22, 26, 146, 120, 66);
    ctx.stroke();
    if (lit) glow(ctx, 95, 90, 140, P.amber2, 0.35);
    else glow(ctx, 95, 90, 70, P.amber1, 0.15);
  },
});

/** Kachelofen / Kanonenofen */
reg('stove', {
  size: (p) => ({ w: 170, h: 360 + (p.pipe ?? 0) }),
  draw(ctx, p, rng) {
    const pipe = p.pipe ?? 0;
    if (pipe) {
      ctx.fillStyle = P.iron0;
      ctx.fillRect(70, 0, 30, pipe + 10);
      ctx.fillStyle = P.iron1;
      ctx.fillRect(70, 0, 8, pipe + 10);
      ctx.fillStyle = P.iron2;
      ctx.fillRect(64, pipe * 0.4, 42, 8);
      ctx.translate(0, pipe);
    }
    contactShadow(ctx, 85, 354, 170, 28, 0.6);
    // Rohr
    ctx.fillStyle = P.iron0;
    ctx.fillRect(70, 0, 30, 140);
    ctx.fillStyle = P.iron1;
    ctx.fillRect(70, 0, 8, 140);
    // Körper
    ctx.fillStyle = P.iron0;
    ctx.beginPath();
    ctx.moveTo(40, 140);
    ctx.quadraticCurveTo(10, 240, 30, 320);
    ctx.lineTo(140, 320);
    ctx.quadraticCurveTo(160, 240, 130, 140);
    ctx.closePath();
    ctx.fill();
    hgrad(ctx, 20, 140, 140, 180, [[0, rgba(P.iron2, 0.35)], [0.4, rgba(P.iron2, 0)], [1, rgba(P.night0, 0.4)]]);
    // Füße
    ctx.fillStyle = P.iron0;
    ctx.fillRect(34, 316, 20, 38);
    ctx.fillRect(116, 316, 20, 38);
    // Ofentür mit Glut
    const lit = p.state !== 'cold';
    ctx.fillStyle = P.iron1;
    roundRect(ctx, 54, 220, 62, 54, 8);
    ctx.fill();
    ctx.fillStyle = lit ? P.red2 : P.ink;
    roundRect(ctx, 62, 232, 46, 30, 6);
    ctx.fill();
    if (lit) {
      for (let i = 0; i < 10; i++) {
        ctx.fillStyle = rng.pick([P.amber2, P.amber1, P.red2]);
        ctx.beginPath();
        ctx.arc(66 + rng.next() * 38, 250 + rng.next() * 10, rng.range(2, 5), 0, Math.PI * 2);
        ctx.fill();
      }
      glow(ctx, 85, 248, 90, P.amber1, 0.35);
    }
    ctx.fillStyle = P.brass1;
    ctx.fillRect(56, 150, 58, 8);
  },
});

/** Hängelampe (nur Körper; Licht kommt dynamisch) */
reg('lamp', {
  size: (p) => ({ w: 120, h: (p.cord ?? 160) + 70, ax: 60, ay: 0 }),
  draw(ctx, p) {
    const cord = p.cord ?? 160;
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(60, 0);
    ctx.lineTo(60, cord);
    ctx.stroke();
    ctx.fillStyle = p.color === 'green' ? P.green1 : P.brass1;
    ctx.beginPath();
    ctx.moveTo(44, cord);
    ctx.lineTo(76, cord);
    ctx.lineTo(108, cord + 46);
    ctx.lineTo(12, cord + 46);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = rgba(P.amber3, 0.3);
    ctx.fillRect(30, cord + 10, 10, 30);
    ctx.fillStyle = P.amber3;
    ctx.beginPath();
    ctx.ellipse(60, cord + 48, 26, 10, 0, 0, Math.PI * 2);
    ctx.fill();
  },
});

/** Fass */
reg('barrel', {
  size: () => ({ w: 150, h: 190 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 75, 184, 150, 24, 0.55);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(20, 20);
    ctx.quadraticCurveTo(0, 95, 20, 175);
    ctx.lineTo(130, 175);
    ctx.quadraticCurveTo(150, 95, 130, 20);
    ctx.closePath();
    ctx.clip();
    for (let i = 0; i < 7; i++) woodGrain(ctx, rng, 4 + i * 20, 10, 20, 170, shade(P.wood2, rng.range(-0.1, 0.06)), false);
    hgrad(ctx, 0, 0, 150, 190, [[0, rgba(P.night0, 0.5)], [0.4, rgba(P.amber2, 0.12)], [1, rgba(P.night0, 0.6)]]);
    ctx.restore();
    for (const y of [36, 92, 154]) {
      ctx.fillStyle = P.iron1;
      ctx.fillRect(4, y, 142, 9);
      ctx.fillStyle = P.iron2;
      ctx.fillRect(4, y, 142, 2);
    }
    ctx.fillStyle = P.wood1;
    ctx.beginPath();
    ctx.ellipse(75, 20, 56, 12, 0, 0, Math.PI * 2);
    ctx.fill();
    if (p.label) text(ctx, p.label, 75, 122, `700 18px ${FONT_HAND}`, rgba(P.paper, 0.8));
  },
});

/** Holzkiste */
reg('crate', {
  size: (p) => ({ w: (p.w ?? 140) + 10, h: (p.h ?? 110) + 10 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 140, h = p.h ?? 110;
    contactShadow(ctx, w / 2 + 5, h + 4, w + 20, 18, 0.5);
    for (let i = 0; i < 4; i++) woodGrain(ctx, rng, 5, 5 + (i * h) / 4, w, h / 4 - 2, shade(P.wood3, rng.range(-0.15, 0.05)), true);
    ctx.strokeStyle = P.wood1;
    ctx.lineWidth = 8;
    ctx.strokeRect(9, 9, w - 8, h - 8);
    ctx.beginPath();
    ctx.moveTo(9, 9);
    ctx.lineTo(w + 1, h + 1);
    ctx.stroke();
    if (p.label) text(ctx, p.label, w / 2 + 5, h / 2 + 5, `700 20px ${FONT_DISPLAY}`, rgba(P.ink, 0.6));
  },
});

/** Spinnennetz für Ecken */
reg('cobweb', {
  size: (p) => ({ w: p.s ?? 140, h: p.s ?? 140, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const s = p.s ?? 140;
    ctx.strokeStyle = rgba(P.white, 0.25);
    ctx.lineWidth = 1;
    for (let i = 0; i < 7; i++) {
      const a = (i / 6) * (Math.PI / 2);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(a) * s, Math.sin(a) * s);
      ctx.stroke();
    }
    for (let r = 20; r < s; r += rng.range(14, 24)) {
      ctx.beginPath();
      for (let i = 0; i <= 6; i++) {
        const a = (i / 6) * (Math.PI / 2);
        const rr = r * (0.9 + 0.1 * Math.sin(i * 2));
        if (i === 0) ctx.moveTo(Math.cos(a) * rr, Math.sin(a) * rr);
        else ctx.quadraticCurveTo(Math.cos(a - 0.13) * rr * 0.92, Math.sin(a - 0.13) * rr * 0.92, Math.cos(a) * rr, Math.sin(a) * rr);
      }
      ctx.stroke();
    }
  },
});

/** Topfpflanze (Gummibaum, halb vertrocknet) */
reg('plant', {
  size: () => ({ w: 160, h: 260 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 80, 254, 110, 18, 0.5);
    ctx.fillStyle = P.red1;
    poly(ctx, [40, 190, 120, 190, 110, 256, 50, 256]);
    ctx.fill();
    ctx.fillStyle = P.red2;
    ctx.fillRect(36, 184, 88, 14);
    ctx.strokeStyle = P.wood1;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(80, 186);
    ctx.quadraticCurveTo(70, 120, 84, 40);
    ctx.stroke();
    for (let i = 0; i < 11; i++) {
      const t = i / 11;
      const y = 180 - t * 140, x = 80 - t * 4;
      const dir = i % 2 ? 1 : -1;
      const dry = p.dry ? rng.chance(0.5) : false;
      ctx.fillStyle = dry ? P.amber0 : rng.pick([P.green1, P.green2, P.green0]);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(dir * (0.7 + rng.range(-0.2, 0.3)) + (dry ? dir * 0.6 : 0));
      ctx.beginPath();
      ctx.ellipse(0, -30, 13, 32, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  },
});

/** Kerze (eigenes Objekt für Fensterbank etc.) */
reg('candle', {
  size: () => ({ w: 40, h: 70 }),
  draw(ctx) {
    ctx.fillStyle = P.paper;
    ctx.fillRect(13, 22, 14, 48);
    ctx.fillStyle = P.amber3;
    ctx.beginPath();
    ctx.ellipse(20, 12, 5, 11, 0, 0, Math.PI * 2);
    ctx.fill();
  },
});

/** Ankerwappen / Wirtshausschild innen */
reg('sign', {
  size: (p) => ({ w: p.w ?? 420, h: 110 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 420;
    ctx.fillStyle = rgba(P.night0, 0.5);
    roundRect(ctx, 8, 12, w - 8, 96, 14);
    ctx.fill();
    ctx.save();
    roundRect(ctx, 0, 0, w - 8, 96, 14);
    ctx.clip();
    woodGrain(ctx, rng, 0, 0, w, 96, P.wood1, true);
    ctx.restore();
    ctx.strokeStyle = P.brass1;
    ctx.lineWidth = 3;
    roundRect(ctx, 8, 8, w - 24, 80, 10);
    ctx.stroke();
    text(ctx, p.text ?? '', (w - 8) / 2, 50, `700 40px ${FONT_DISPLAY}`, P.brass2);
  },
});

/** Kellerregal mit Einmachgläsern und Kram */
reg('storageshelf', {
  size: (p) => ({ w: p.w ?? 340, h: 420 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 340;
    contactShadow(ctx, w / 2, 414, w + 30, 22, 0.55);
    ctx.fillStyle = P.wood1;
    ctx.fillRect(0, 0, 14, 420);
    ctx.fillRect(w - 14, 0, 14, 420);
    for (let r = 0; r < 4; r++) {
      const y = 90 + r * 105;
      woodGrain(ctx, rng, 0, y, w, 14, P.wood2, true);
      let x = 22;
      while (x < w - 50) {
        const kind = rng.int(0, 2);
        if (kind === 0) {
          const jw = rng.range(30, 44), jh = rng.range(40, 60);
          ctx.fillStyle = rgba(rng.pick([P.amber1, P.red1, P.green2]), 0.75);
          roundRect(ctx, x, y - jh, jw, jh, 6);
          ctx.fill();
          ctx.fillStyle = P.iron2;
          ctx.fillRect(x + 2, y - jh - 6, jw - 4, 7);
          ctx.fillStyle = rgba(P.white, 0.3);
          ctx.fillRect(x + 5, y - jh + 6, 4, jh - 12);
          x += jw + 10;
        } else if (kind === 1) {
          const bw = rng.range(50, 80), bh = rng.range(30, 55);
          ctx.fillStyle = shade(P.plaster1, rng.range(-0.2, 0.1));
          ctx.fillRect(x, y - bh, bw, bh);
          ctx.fillStyle = rgba(P.ink, 0.25);
          ctx.fillRect(x, y - bh, bw, 5);
          x += bw + 8;
        } else {
          for (let b = 0; b < 4; b++) {
            ctx.fillStyle = rng.pick([P.red0, P.sea0, P.green0, P.wood3]);
            ctx.fillRect(x + b * 12, y - 70, 11, 70);
          }
          x += 56;
        }
      }
    }
    ctx.fillStyle = rgba(P.night0, 0.25);
    ctx.fillRect(0, 0, w, 420);
  },
});

/** Seekiste (Truhe) mit Zuständen closed/open */
reg('chest', {
  size: () => ({ w: 230, h: 170 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 115, 164, 230, 24, 0.55);
    const open = p.state === 'open';
    woodGrain(ctx, rng, 15, 60, 200, 100, P.wood2, true);
    ctx.fillStyle = P.iron1;
    ctx.fillRect(15, 60, 200, 10);
    ctx.fillRect(40, 60, 12, 100);
    ctx.fillRect(178, 60, 12, 100);
    if (open) {
      ctx.fillStyle = P.night0;
      ctx.fillRect(20, 50, 190, 16);
      woodGrain(ctx, rng, 15, 0, 200, 50, P.wood1, true);
      glow(ctx, 115, 60, 60, P.amber2, 0.2);
    } else {
      ctx.fillStyle = P.wood3;
      ctx.beginPath();
      ctx.moveTo(15, 64);
      ctx.quadraticCurveTo(115, 10, 215, 64);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = P.brass1;
      ctx.fillRect(104, 62, 22, 28);
      ctx.fillStyle = P.ink;
      ctx.fillRect(112, 72, 6, 10);
    }
    edgeLight(ctx, 15, 60, 200, 100, P.wood2);
  },
});

/** Brief am Boden / auf Tisch (einfacher Gegenstand) */
reg('letter', {
  size: () => ({ w: 70, h: 40 }),
  draw(ctx) {
    ctx.save();
    ctx.translate(35, 22);
    ctx.rotate(-0.15);
    ctx.fillStyle = P.paper;
    ctx.fillRect(-30, -16, 60, 32);
    ctx.strokeStyle = P.red1;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-30, -16);
    ctx.lineTo(0, 4);
    ctx.lineTo(30, -16);
    ctx.stroke();
    ctx.restore();
  },
});

/** Mauerwerk-Hintergrund für Keller/Lager */
reg('cellar', {
  size: (p) => ({ w: p.width ?? 1920, h: p.height ?? 1080, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const W = p.width ?? 1920, H = p.height ?? 1080, floorY = p.floorY ?? 720;
    vgrad(ctx, 0, 0, W, floorY, [[0, shade(P.plaster0, -0.7)], [1, shade(P.plaster0, -0.45)]]);
    // Ziegel
    const bw = 96, bh = 42;
    for (let row = 0; row * bh < floorY; row++) {
      for (let col = -1; col * bw < W; col++) {
        const x = col * bw + (row % 2 ? bw / 2 : 0) + rng.range(-2, 2), y = row * bh;
        const base = shade(rng.pick([P.red0, P.red1, P.wood2, P.plaster0]), rng.range(-0.55, -0.3));
        ctx.fillStyle = base;
        roundRect(ctx, x + 3, y + 3, bw - 6, bh - 6, 4);
        ctx.fill();
        ctx.fillStyle = rgba(P.amber3, 0.05);
        ctx.fillRect(x + 5, y + 4, bw - 10, 3);
      }
    }
    dabs(ctx, rng, 0, 0, W, floorY, [P.plaster0, P.night1, P.green0], { count: 1600, size: [6, 24], alpha: [0.04, 0.14] });
    // Salpeter-Ausblühungen
    for (let i = 0; i < 8; i++) radial(ctx, rng.range(0, W), rng.range(floorY * 0.5, floorY), rng.range(60, 140), [[0, rgba(P.paper, 0.08)], [1, rgba(P.paper, 0)]]);
    grain(ctx, 0, 0, W, floorY, 0.18);
    vgrad(ctx, 0, 0, W, 320, [[0, rgba(P.night0, 0.75)], [1, rgba(P.night0, 0)]]);
    // Holzbalken an der Decke
    woodGrain(ctx, rng, 0, 0, W, 40, P.wood0, true);
    for (let x = 140; x < W; x += 420) {
      woodGrain(ctx, rng, x, 0, 60, 70, P.wood1, false);
    }
    // Boden: Kopfstein, dunkel
    vgrad(ctx, 0, floorY, W, H - floorY, [[0, shade(P.iron0, -0.3)], [1, shade(P.iron1, -0.1)]]);
    for (let i = 0; i < 260; i++) {
      const t = rng.next();
      const y = floorY + Math.pow(t, 1.2) * (H - floorY);
      const s = 14 + ((y - floorY) / (H - floorY)) * 46;
      const x = rng.next() * W;
      ctx.fillStyle = shade(P.iron1, rng.range(-0.35, 0.02));
      ctx.beginPath();
      ctx.ellipse(x, y, s, s * 0.32, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = rgba(P.amber3, 0.06);
      ctx.beginPath();
      ctx.ellipse(x - s * 0.15, y - s * 0.08, s * 0.6, s * 0.14, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    grain(ctx, 0, floorY, W, H - floorY, 0.2);
    vgrad(ctx, 0, floorY, W, 100, [[0, rgba(P.night0, 0.6)], [1, rgba(P.night0, 0)]]);
    for (const d of p.doors ?? []) drawDoor(ctx, rng, { ...d, floorY });
    for (const win of p.windows ?? []) drawWindow(ctx, rng, win);
    hgrad(ctx, 0, 0, 300, H, [[0, rgba(P.night0, 0.6)], [1, rgba(P.night0, 0)]]);
    hgrad(ctx, W - 300, 0, 300, H, [[0, rgba(P.night0, 0)], [1, rgba(P.night0, 0.6)]]);
  },
});

// Inventar-Icons (klein, 96x96)
reg('item', {
  size: () => ({ w: 96, h: 96, ax: 48, ay: 48 }),
  draw(ctx, p, rng) {
    const k = p.kind;
    ctx.save();
    if (k === 'key') {
      ctx.translate(48, 48);
      ctx.rotate(-0.6);
      ctx.fillStyle = P.brass1;
      ctx.beginPath();
      ctx.arc(-22, 0, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.night1;
      ctx.beginPath();
      ctx.arc(-22, 0, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.brass1;
      ctx.fillRect(-10, -4, 44, 8);
      ctx.fillRect(22, 4, 6, 12);
      ctx.fillRect(30, 4, 5, 8);
      ctx.fillStyle = P.brass2;
      ctx.fillRect(-10, -4, 44, 2);
    } else if (k === 'letter') {
      ctx.translate(48, 48);
      ctx.rotate(-0.1);
      ctx.fillStyle = P.paper;
      ctx.fillRect(-34, -22, 68, 44);
      ctx.strokeStyle = P.red1;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(-34, -22);
      ctx.lineTo(0, 4);
      ctx.lineTo(34, -22);
      ctx.stroke();
      ctx.fillStyle = P.red1;
      ctx.beginPath();
      ctx.arc(0, 4, 7, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillStyle = P.brass1;
      ctx.beginPath();
      ctx.arc(48, 48, 28, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    void rng;
  },
});
